import React, { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { getsessionuser } from './../Utils/sessions';
import Universalheader from "../Headers/universalheader";
import {
    Box, Paper, Typography, Breadcrumbs, Link as MuiLink, Divider
} from "@mui/material";
import { GavelRounded } from "@mui/icons-material";

// ─── Design tokens — kept consistent with Help.jsx / Faqs ─────────────────
const BRAND = '#900f20';
const INK = '#1c1a1a';
const CANVAS = '#f6f5f3';
const MUTED = '#78726f';
const SERIF = 'Georgia, "Times New Roman", serif';

// ─── Section content — same copy as before, split into sections for the TOC ─
const sections = [
    {
        id: 'personal-information',
        title: 'Personal Information',
        body: (
            <>
                We collect certain information from you as you use the Eazistey Site, including your name, email address,
                phone number and location. The information collected is used in the following ways:
                <br /><br />
                <b>Email address:</b> we use your email address to update you on any changes made to the Site, send
                notifications from the Site and help in account recovery.
            </>
        ),
    },
    {
        id: 'location',
        title: 'Location',
        body: (
            <>
                We pin the area for the property's account to help prospective clients have an easier time getting to the
                property. We then use the user's current location to help them get directions to the properties.
            </>
        ),
    },
    {
        id: 'device-information',
        title: 'Device Information',
        body: (
            <>
                Our website chiefly operates in a specified region, i.e. in Kenya, around metropolitans. We use the Device
                Information that we collect to help us screen for potential risk and fraud (in particular, your IP address)
                and generally improve and optimize our Site. For example, generating analytics about how our customers
                browse and interact with the Site and assessing the success of our advertising campaigns.
            </>
        ),
    },
    {
        id: 'sharing-your-personal-information',
        title: 'Sharing Your Personal Information',
        body: (
            <>
                We share your Personal Information with third parties to help us use your Personal Information, as
                described above. For example, we use Google Analytics to help us understand how our customers use the
                Site — you can read more about how Google uses your Personal Information{' '}
                <MuiLink href="https://www.google.com/intl/en/policies/privacy/" target="_blank" rel="noopener noreferrer" sx={{ color: BRAND, fontWeight: 600 }}>
                    here
                </MuiLink>. You can also opt-out of Google Analytics{' '}
                <MuiLink href="https://tools.google.com/dlpage/gaoptout" target="_blank" rel="noopener noreferrer" sx={{ color: BRAND, fontWeight: 600 }}>
                    here
                </MuiLink>.
                <br /><br />
                Finally, we may also share your Personal Information to comply with applicable laws and regulations,
                respond to a subpoena, search warrant, or another lawful request for information we receive, or otherwise
                protect our rights.
            </>
        ),
    },
    {
        id: 'behavioural-advertising',
        title: 'Behavioural Advertising',
        body: (
            <>
                As described above, we use your Personal Information to provide you with targeted advertisements or
                marketing communications we believe may interest you. For more information about how targeted advertising
                works, you can visit the Network Advertising Initiative's ("NAI") educational page{' '}
                <MuiLink
                    href="http://www.networkadvertising.org/understanding-online-advertising/how-does-it-work"
                    target="_blank" rel="noopener noreferrer" sx={{ color: BRAND, fontWeight: 600 }}
                >
                    here
                </MuiLink>.
            </>
        ),
    },
    {
        id: 'do-not-track',
        title: 'Do Not Track',
        body: (
            <>
                Please note that we do not alter our Site's data collection and use practices when we see a Do Not Track
                signal from your browser.
            </>
        ),
    },
    {
        id: 'mobile-app-accessibility',
        title: 'Mobile App Accessibility',
        body: (
            <>
                <b>Camera</b><br />
                Eazistey needs access to the user's camera for users to update their profile photos, photos of their
                products and services, and photos of their properties on the platform.
                <br /><br />
                <b>File Manager</b><br />
                We request access to the user's files for users who already have photos of the products they'd like to
                upload to the site to do so easily.
            </>
        ),
    },
    {
        id: 'your-rights',
        title: 'Your Rights',
        body: (
            <>
                You have the right to access the personal information we hold about you and ask that your personal
                information be corrected, updated, or deleted. If you would like to exercise this right, don't hesitate to
                contact us through the contact information below.
                <br /><br />
                Additionally, suppose you are a Kenyan resident. In that case, we note that we are processing your
                information to fulfill contracts we might have with you (for example, if you make an order through the
                Site) or otherwise to pursue our legitimate business interests listed above.
            </>
        ),
    },
    {
        id: 'changes',
        title: 'Changes',
        body: (
            <>
                We may update this privacy policy from time to time to reflect, for example, changes to our practices or
                for other operational, legal or regulatory reasons.
            </>
        ),
    },
    {
        id: 'contact-us',
        title: 'Contact Us',
        body: (
            <>
                For more information about our privacy practices, if you have questions, or if you would like to make a
                complaint, don't hesitate to get in touch with us by email at{' '}
                <MuiLink href="mailto:admin@eazistey.co.ke" sx={{ color: BRAND, fontWeight: 600 }}>
                    admin@eazistey.co.ke
                </MuiLink>.
            </>
        ),
    },
];

const scrollToSection = (id) => {
    document.getElementById(id)?.scrollIntoView({ behavior: 'smooth', block: 'start' });
};

const Terms = () => {

    const navigate = useNavigate();

    const sessionuser = getsessionuser();
    const [user] = useState(sessionuser);

    return (
        <Box sx={{ bgcolor: CANVAS, minHeight: '100vh', pb: 10 }}>
            <Universalheader />
            <Box sx={{ width: "90%", margin: "auto" }}>
                {/* Breadcrumb */}
                <Box sx={{ px: { xs: 2, md: 6 }, mt: 3, mb: 1 }}>
                    <Breadcrumbs separator="›" sx={{ '& .MuiBreadcrumbs-separator': { color: 'rgba(28,26,26,0.3)' } }}>
                        <MuiLink
                            component="button" underline="hover" onClick={() => navigate('/')}
                            sx={{ color: BRAND, fontWeight: 600, fontSize: '0.875rem' }}
                        >
                            Home
                        </MuiLink>
                        <MuiLink
                            component="button" underline="hover" onClick={() => navigate(-1)}
                            sx={{ color: BRAND, fontWeight: 600, fontSize: '0.875rem' }}
                        >
                            Properties
                        </MuiLink>
                        <Typography sx={{ color: INK, fontWeight: 600, fontSize: '0.875rem' }}>Terms</Typography>
                    </Breadcrumbs>
                </Box>

                {/* Hero */}
                <Box sx={{ px: { xs: 2, md: 6 }, pt: { xs: 3, md: 4 }, pb: { xs: 2, md: 3 } }}>
                    <Box sx={{
                        display: 'inline-flex', alignItems: 'center', gap: 1, mb: 2,
                        px: 2, py: 0.75, borderRadius: '999px', bgcolor: 'rgba(144,15,32,0.06)',
                    }}>
                        <GavelRounded sx={{ fontSize: 18, color: BRAND }} />
                        <Typography variant="body2" sx={{ color: BRAND, fontWeight: 700 }}>
                            Legal
                        </Typography>
                    </Box>
                    <Typography sx={{
                        fontFamily: SERIF, fontWeight: 700, color: INK, lineHeight: 1.15,
                        fontSize: { xs: '1.9rem', sm: '2.4rem', md: '2.75rem' }, mb: 1.5,
                    }}>
                        Terms & conditions, and{' '}
                        <Box component="span" sx={{ fontStyle: 'italic', color: BRAND }}>privacy policy</Box>
                    </Typography>
                    <Typography sx={{ color: MUTED, fontSize: '1.02rem', maxWidth: 640 }}>
                        This Privacy Policy describes how your personal information is collected, used, and shared when you
                        visit eazistey.co.ke{user?.username ? `, ${user.username}` : ''}.
                    </Typography>
                </Box>

                {/* Content */}
                <Box sx={{
                    px: { xs: 2, md: 6 }, mt: 2,
                    display: 'flex', flexDirection: { xs: 'column', md: 'row' },
                    alignItems: 'flex-start', gap: { xs: 3, md: 5 },
                }}>

                    {/* Sticky table of contents */}
                    <Box
                        component="nav"
                        aria-label="Policy sections"
                        sx={{
                            display: { xs: 'none', md: 'block' },
                            width: 240, flexShrink: 0,
                            position: 'sticky', top: 24,
                        }}
                    >
                        <Paper
                            elevation={0}
                            sx={{
                                borderRadius: '18px', border: '1px solid rgba(28,26,26,0.07)',
                                p: 2.5, boxShadow: '0 8px 24px rgba(28,26,26,0.05)',
                            }}
                        >
                            <Typography sx={{ fontWeight: 700, color: INK, fontSize: '0.85rem', mb: 1.5, textTransform: 'uppercase', letterSpacing: '0.04em' }}>
                                On this page
                            </Typography>
                            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                                {sections.map((s) => (
                                    <MuiLink
                                        key={s.id}
                                        component="button"
                                        underline="none"
                                        onClick={() => scrollToSection(s.id)}
                                        sx={{
                                            textAlign: 'left', color: MUTED, fontSize: '0.88rem', fontWeight: 500,
                                            '&:hover': { color: BRAND },
                                        }}
                                    >
                                        {s.title}
                                    </MuiLink>
                                ))}
                            </Box>
                        </Paper>
                    </Box>

                    {/* Policy text */}
                    <Paper
                        elevation={0}
                        sx={{
                            flex: 1, width: '100%',
                            borderRadius: '22px', border: '1px solid rgba(28,26,26,0.07)', bgcolor: 'white',
                            p: { xs: 2.5, sm: 4, md: 5 }, boxShadow: '0 12px 32px rgba(28,26,26,0.06)',
                        }}
                    >
                        {sections.map((s, i) => (
                            <Box key={s.id} id={s.id} sx={{ scrollMarginTop: '96px' }}>
                                <Typography sx={{
                                    fontFamily: SERIF, fontWeight: 700, color: INK,
                                    fontSize: { xs: '1.15rem', md: '1.3rem' }, mb: 1.5,
                                }}>
                                    {s.title}
                                </Typography>
                                <Typography sx={{ color: MUTED, fontSize: '0.98rem', lineHeight: 1.85 }}>
                                    {s.body}
                                </Typography>
                                {i < sections.length - 1 && <Divider sx={{ my: 3.5, borderColor: 'rgba(28,26,26,0.06)' }} />}
                            </Box>
                        ))}
                    </Paper>
                </Box>
            </Box>
        </Box>
    )
}

export default Terms;